import { type LogLike } from './crimes-report';
import { dayStart, inRange, sumPerDay, type DateRange } from './log-kit';

/**
 * Your racing report (website roadmap S37 — torn.report's racing page): per track (Uptown … Convict, Torn's names),
 * results (positions, wins), best laps, racing skill and points, the cars you raced — from Torn's racing selections (v2
 * `user/races` with every racer's result, `user/racingrecords`, `user/enlistedcars`, `personalstats?cat=racing`,
 * `racing/tracks`) and your own log's "Racing" lines. Pure and browser-safe; tested in
 * packages/shared/test/racing-report.test.ts.
 *
 * Torn documents the racing selections; it doesn't document its racing log lines' `data`, so those are read leniently
 * (race id, track, position, car, times, skill and points under the usual field names) and never guessed.
 */

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const list = (x: unknown): unknown[] => (Array.isArray(x) ? x : obj(x) ? Object.values(x as Obj) : []);
const num = (x: unknown): number | null => {
  if (typeof x === 'number' && Number.isFinite(x)) return x;
  if (typeof x === 'string' && x.trim() !== '' && Number.isFinite(Number(x.replace(/[$,]/g, '')))) return Number(x.replace(/[$,]/g, ''));
  return null;
};
const str = (x: unknown): string | null => (typeof x === 'string' && x.trim() ? x.trim() : null);
const firstOf = (d: Obj, keys: readonly string[]): unknown => {
  for (const k of keys) if (d[k] !== undefined && d[k] !== null && d[k] !== '') return d[k];
  return undefined;
};
/** Seconds or milliseconds → ms. */
const ms = (t: number | null): number | null => (t === null || t <= 0 ? null : t < 1e11 ? t * 1000 : t);

// ---------------------------------------------------------------- tracks

export interface RaceTrack {
  id: number;
  name: string;
  description?: string;
}

/**
 * Torn's tracks in Torn's own order (the Racing page lists them Uptown … Convict). The names and ids come from
 * `racing/tracks` when it answers; this copy keeps the order and fills in when it doesn't.
 */
export const RACE_TRACKS: readonly RaceTrack[] = [
  { id: 6, name: 'Uptown' },
  { id: 7, name: 'Withdrawal' },
  { id: 8, name: 'Underdog' },
  { id: 9, name: 'Parkland' },
  { id: 10, name: 'Docks' },
  { id: 11, name: 'Commerce' },
  { id: 12, name: 'Two Islands' },
  { id: 15, name: 'Industrial' },
  { id: 16, name: 'Vector' },
  { id: 17, name: 'Mudpit' },
  { id: 18, name: 'Hammerhead' },
  { id: 19, name: 'Sewage' },
  { id: 20, name: 'Meltdown' },
  { id: 21, name: 'Speedway' },
  { id: 23, name: 'Stone Park' },
  { id: 24, name: 'Convict' },
];

const loose = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/** v2 `racing/tracks` → Torn's tracks (built-in order first, then any new ones Torn adds; none answered → the built-in list). */
export function readRaceTracks(body: unknown): RaceTrack[] {
  const got = list(obj(body)?.tracks)
    .map(obj)
    .map((t) => ({ id: num(t?.id) ?? 0, name: str(t?.title) ?? str(t?.name) ?? '', description: str(t?.description) ?? '' }))
    .filter((t) => t.id > 0 && t.name);
  if (!got.length) return [...RACE_TRACKS];
  const rank = (t: RaceTrack) => {
    const i = RACE_TRACKS.findIndex((b) => b.id === t.id || loose(b.name) === loose(t.name));
    return i < 0 ? 1000 + t.id : i;
  };
  return got.sort((a, b) => rank(a) - rank(b));
}

/** A track by id, or by name in a log line ("Two Islands", "two_islands", 12). */
export function trackOf(x: unknown, tracks: readonly RaceTrack[] = RACE_TRACKS): RaceTrack | null {
  const n = num(x);
  if (n !== null) return tracks.find((t) => t.id === n) ?? RACE_TRACKS.find((t) => t.id === n) ?? { id: n, name: `Track ${n}` };
  const s = str(x);
  if (!s) return null;
  const k = loose(s);
  return tracks.find((t) => loose(t.name) === k) ?? RACE_TRACKS.find((t) => loose(t.name) === k) ?? null;
}

// ---------------------------------------------------------------- your races (v2 user/races)

export interface MyRace {
  /** Torn's race id (null: a log line that doesn't give one). */
  id: number | null;
  /** When it ended (else started), ms. */
  at: number;
  title: string;
  trackId: number | null;
  laps: number | null;
  /** Official (Torn's) race, a custom one, or not known. */
  official: boolean | null;
  /** Your place (null: crashed or not given). */
  position: number | null;
  /** How many raced. */
  racers: number | null;
  car: string | null;
  carClass: string | null;
  crashed: boolean;
  /** Seconds. */
  time: number | null;
  bestLap: number | null;
  /** Racing skill this race gave (your log), null when not said. */
  skill: number | null;
  /** Racing points this race gave (your log), null when not said. */
  points: number | null;
  /** Where it was read: Torn's race list, your log, or both. */
  from: 'races' | 'log' | 'both';
}

/** One race of v2 `user/races` → your part of it (null: not a race, not finished, or you're not in its results). */
export function readMyRace(x: unknown, me: number): MyRace | null {
  const r = obj(x);
  const id = num(r?.id);
  if (!r || !id) return null;
  const status = str(r.status);
  if (status && status !== 'finished') return null;
  const sched = obj(r.schedule) ?? {};
  const at = ms(num(sched.end) ?? num(sched.start) ?? num(r.end) ?? num(r.start));
  const results = list(r.results).map(obj).filter((o): o is Obj => !!o);
  const mine = results.find((o) => num(o.driver_id ?? o.user_id ?? o.id) === me);
  if (!at || !mine) return null;
  const crashed = mine.has_crashed === true || mine.crashed === true;
  return {
    id,
    at,
    title: str(r.title) ?? `Race ${id}`,
    trackId: num(r.track_id),
    laps: num(r.laps),
    official: typeof r.is_official === 'boolean' ? r.is_official : null,
    position: crashed ? null : num(mine.position),
    racers: results.length || num(obj(r.participants)?.current),
    car: str(mine.car_item_name) ?? str(mine.car_name),
    carClass: str(mine.car_class),
    crashed,
    time: crashed ? null : num(mine.race_time),
    bestLap: num(mine.best_lap_time),
    skill: null,
    points: null,
    from: 'races',
  };
}

/** v2 `user/races` → your finished races, newest first. */
export function readMyRaces(body: unknown, me: number): MyRace[] {
  return list(obj(body)?.races)
    .map((r) => readMyRace(r, me))
    .filter((r): r is MyRace => !!r)
    .sort((a, b) => b.at - a.at);
}

/** The oldest start in a `user/races` answer (unix seconds) — "read older" asks Torn for races before it. */
export function oldestRaceStart(body: unknown): number | null {
  let low: number | null = null;
  for (const r of list(obj(body)?.races).map(obj)) {
    const s = num(obj(r?.schedule)?.start) ?? num(r?.start);
    if (s !== null && (low === null || s < low)) low = s;
  }
  return low;
}

// ---------------------------------------------------------------- records, cars, personal stats

export interface LapRecord {
  trackId: number;
  track: string;
  car: string;
  carItemId: number | null;
  /** Seconds. */
  lap: number;
}

/** Torn gives some lap times in milliseconds: anything over 1,000 is read as such (no lap takes 17 minutes). */
const lapSec = (x: unknown): number | null => {
  const n = num(x);
  if (n === null || n <= 0) return null;
  return n > 1000 ? n / 1000 : n;
};

/** v2 `user/racingrecords` → your best lap per track and car. */
export function readRacingRecords(body: unknown): LapRecord[] {
  const out: LapRecord[] = [];
  for (const t of list(obj(body)?.racingrecords).map(obj)) {
    const track = obj(t?.track);
    const trackId = num(track?.id);
    if (!trackId) continue;
    for (const r of list(t?.records).map(obj)) {
      const lap = lapSec(r?.lap_time ?? r?.lap);
      if (lap === null) continue;
      out.push({ trackId, track: str(track?.name) ?? `Track ${trackId}`, car: str(r?.car_name) ?? 'A car', carItemId: num(r?.car_id), lap });
    }
  }
  return out.sort((a, b) => a.trackId - b.trackId || a.lap - b.lap);
}

export interface EnlistedCar {
  id: number;
  carItemId: number | null;
  /** The car's own name if you gave it one, else Torn's. */
  name: string;
  model: string;
  carClass: string | null;
  entered: number;
  won: number;
  removed: boolean;
  worth: number | null;
  pointsSpent: number | null;
}

/** v2 `user/enlistedcars` → your cars, most raced first. */
export function readEnlistedCars(body: unknown): EnlistedCar[] {
  return list(obj(body)?.enlistedcars)
    .map(obj)
    .filter((c): c is Obj => !!c && num(c.id) !== null)
    .map((c) => {
      const model = str(c.car_item_name) ?? 'A car';
      return { id: num(c.id)!, carItemId: num(c.car_item_id), name: str(c.name) ?? model, model, carClass: str(c.class), entered: num(c.races_entered) ?? 0, won: num(c.races_won) ?? 0, removed: c.is_removed === true, worth: num(c.worth), pointsSpent: num(c.points_spent) };
    })
    .sort((a, b) => b.entered - a.entered || a.name.localeCompare(b.name));
}

export interface RacingStats {
  skill: number | null;
  points: number | null;
  entered: number | null;
  won: number | null;
}

/** Personal stats (v2 `personalstats?cat=racing`, or v1's racingskill / racingpointsearned / racesentered / raceswon). */
export function readRacingStats(body: unknown): RacingStats | null {
  const ps = obj(obj(body)?.personalstats) ?? obj(body);
  const r = obj(ps?.racing);
  if (r) {
    const races = obj(r.races) ?? {};
    return { skill: num(r.skill), points: num(r.points), entered: num(races.entered), won: num(races.won) };
  }
  if (!ps) return null;
  const v1 = { skill: num(ps.racingskill), points: num(ps.racingpointsearned), entered: num(ps.racesentered), won: num(ps.raceswon) };
  return Object.values(v1).some((v) => v !== null) ? v1 : null;
}

// ---------------------------------------------------------------- your log's racing lines

export type RaceLogKind = 'finish' | 'join' | 'leave' | 'skill' | 'points' | 'car' | 'other';

export interface RaceLogLine {
  id: string;
  at: number;
  kind: RaceLogKind;
  title: string;
  raceId: number | null;
  trackId: number | null;
  track: string | null;
  position: number | null;
  racers: number | null;
  car: string | null;
  crashed: boolean;
  time: number | null;
  bestLap: number | null;
  skill: number | null;
  points: number | null;
}

/** Is this a racing line? (Torn's "Racing" category, or a title that starts with race/racing.) */
export function isRacingLog(l: Pick<LogLike, 'title' | 'category'>): boolean {
  return /^rac(e|es|ing)$/i.test((l.category ?? '').trim()) || /^rac(e|ing)\b/i.test((l.title ?? '').trim());
}

/** What a racing line did, from its title. */
export function raceLogKind(title: string): RaceLogKind {
  const t = title.toLowerCase();
  if (/\bskill\b/.test(t)) return 'skill';
  if (/\bpoints?\b/.test(t) && !/\bfinish|\bwin|\bwon|\bcomplete/.test(t)) return 'points';
  if (/\b(finish|finished|complete|completed|result|win|won|lost|crash|crashed|place[ds]?)\b/.test(t)) return 'finish';
  if (/\b(join|joined|enter|entered|create|created|start|started)\b/.test(t)) return 'join';
  if (/\b(leave|left|quit|cancel)/.test(t)) return 'leave';
  if (/\b(car|upgrade|part|enlist|buy|bought|sell|sold|remove|parts)\b/.test(t)) return 'car';
  return 'other';
}

const SKILL = ['racing_skill_increase', 'racing_skill_increased', 'racing_skill_gained', 'skill_increase', 'skill_increased', 'skill_gained', 'skill_gain'];
const POINTS = ['racing_points', 'racing_points_gained', 'racing_points_earned', 'points_gained', 'points_earned', 'points'];

/** A log line → what it says about racing (null: not a racing line). */
export function readRaceLogLine(l: LogLike, tracks: readonly RaceTrack[] = RACE_TRACKS): RaceLogLine | null {
  if (!isRacingLog(l)) return null;
  const d = l.data ?? {};
  const title = (l.title ?? '').trim() || l.category || 'Racing';
  let kind = raceLogKind(title);
  const track = trackOf(firstOf(d, ['track', 'track_id', 'track_name', 'trackid']), tracks);
  let skill = num(firstOf(d, SKILL));
  if (skill === null) {
    const before = num(firstOf(d, ['racing_skill_before', 'skill_before']));
    const after = num(firstOf(d, ['racing_skill_after', 'skill_after']));
    if (before !== null && after !== null) skill = Math.round((after - before) * 1e6) / 1e6;
  }
  const points = num(firstOf(d, POINTS));
  const position = num(firstOf(d, ['position', 'place', 'finish_position', 'finished']));
  const crashed = d.crashed === true || d.has_crashed === true || /\bcrash/i.test(title);
  // A line with a place (or a crash) is a race's end, whatever its title says.
  if ((kind === 'other' || kind === 'join') && (position !== null || crashed) && num(firstOf(d, ['race', 'race_id', 'raceid'])) !== null) kind = 'finish';
  const car = firstOf(d, ['car_name', 'car_item_name', 'car', 'vehicle']);
  return {
    id: l.id,
    at: l.at,
    kind,
    title,
    raceId: num(firstOf(d, ['race', 'race_id', 'raceid'])),
    trackId: track?.id ?? null,
    track: track?.name ?? null,
    position: crashed ? null : position,
    racers: num(firstOf(d, ['racers', 'drivers', 'participants', 'entrants'])),
    car: typeof car === 'string' ? str(car) : num(car) !== null ? `Car ${num(car)}` : null,
    crashed,
    time: num(firstOf(d, ['race_time', 'time', 'total_time'])),
    bestLap: lapSec(firstOf(d, ['best_lap', 'best_lap_time', 'lap_time', 'bestlap'])),
    skill: skill === null ? null : Math.max(0, skill),
    points: points === null ? null : Math.max(0, points),
  };
}

export function readRaceLog(lines: readonly LogLike[], tracks: readonly RaceTrack[] = RACE_TRACKS): RaceLogLine[] {
  return lines
    .map((l) => readRaceLogLine(l, tracks))
    .filter((l): l is RaceLogLine => !!l)
    .sort((a, b) => a.at - b.at);
}

/** How near (ms) a skill/points line without a race id must be to a race's end to count for it. */
const NEAR = 10 * 60_000;

/**
 * Torn's race list and your log as one list of races, newest first: a race in both gets the log's skill and points (by
 * race id, else a skill/points line within 10 minutes after it); a finish line Torn's list doesn't have (older than it
 * reaches) becomes a race of its own. Skill and points lines that belong to no race are answered apart (`loose`).
 */
export function mergeRaces(api: readonly MyRace[], log: readonly RaceLogLine[]): { races: MyRace[]; loose: RaceLogLine[] } {
  const byId = new Map<number, MyRace>();
  const out: MyRace[] = [];
  for (const r of api) {
    const c = { ...r };
    out.push(c);
    if (c.id !== null) byId.set(c.id, c);
  }
  const add = (r: MyRace, l: RaceLogLine) => {
    if (l.skill !== null) r.skill = (r.skill ?? 0) + l.skill;
    if (l.points !== null) r.points = (r.points ?? 0) + l.points;
    if (r.from === 'races') r.from = 'both';
  };
  // Finish lines first: they make (or fill in) races.
  for (const l of log.filter((x) => x.kind === 'finish')) {
    const known = l.raceId !== null ? byId.get(l.raceId) : undefined;
    if (known) {
      if (known.trackId === null && l.trackId !== null) known.trackId = l.trackId;
      if (!known.car && l.car) known.car = l.car;
      if (known.bestLap === null && l.bestLap !== null) known.bestLap = l.bestLap;
      add(known, l);
      continue;
    }
    const r: MyRace = { id: l.raceId, at: l.at, title: l.raceId ? `Race ${l.raceId}` : 'A race', trackId: l.trackId, laps: null, official: null, position: l.position, racers: l.racers, car: l.car, carClass: null, crashed: l.crashed, time: l.time, bestLap: l.bestLap, skill: l.skill, points: l.points, from: 'log' };
    out.push(r);
    if (r.id !== null) byId.set(r.id, r);
  }
  const loose: RaceLogLine[] = [];
  const sorted = [...out].sort((a, b) => a.at - b.at);
  for (const l of log.filter((x) => (x.kind !== 'finish' && (x.skill !== null || x.points !== null)))) {
    let r = l.raceId !== null ? byId.get(l.raceId) : undefined;
    if (!r) {
      // The latest race that ended at most 10 minutes before the line (and not after it).
      for (let i = sorted.length - 1; i >= 0; i--) {
        const c = sorted[i]!;
        if (c.at <= l.at + 1000 && l.at - c.at <= NEAR) {
          r = c;
          break;
        }
        if (c.at < l.at - NEAR) break;
      }
    }
    if (r) add(r, l);
    else loose.push(l);
  }
  return { races: out.sort((a, b) => b.at - a.at), loose };
}

// ---------------------------------------------------------------- summing up

const won = (r: MyRace) => r.position === 1;
const podium = (r: MyRace) => r.position !== null && r.position <= 3;

export interface RacingTotals {
  races: number;
  wins: number;
  podiums: number;
  crashes: number;
  /** Wins of races, 0–1 (null: no races). */
  winRate: number | null;
  avgPosition: number | null;
  /** Skill and points your log gave in the range (with lines that belong to no race). */
  skill: number;
  points: number;
  /** Races whose skill/points the log says. */
  skillRaces: number;
}

export function racingTotals(races: readonly MyRace[], loose: readonly RaceLogLine[], r: DateRange): RacingTotals {
  const shown = races.filter((x) => inRange(x.at, r));
  const placed = shown.filter((x) => x.position !== null);
  const looseIn = loose.filter((x) => inRange(x.at, r));
  return {
    races: shown.length,
    wins: shown.filter(won).length,
    podiums: shown.filter(podium).length,
    crashes: shown.filter((x) => x.crashed).length,
    winRate: shown.length ? shown.filter(won).length / shown.length : null,
    avgPosition: placed.length ? placed.reduce((a, x) => a + x.position!, 0) / placed.length : null,
    skill: round(shown.reduce((a, x) => a + (x.skill ?? 0), 0) + looseIn.reduce((a, x) => a + (x.skill ?? 0), 0)),
    points: shown.reduce((a, x) => a + (x.points ?? 0), 0) + looseIn.reduce((a, x) => a + (x.points ?? 0), 0),
    skillRaces: shown.filter((x) => x.skill !== null || x.points !== null).length,
  };
}

const round = (n: number) => Math.round(n * 1e4) / 1e4;

export interface TrackRow {
  trackId: number | null;
  name: string;
  races: number;
  wins: number;
  podiums: number;
  crashes: number;
  avgPosition: number | null;
  bestPosition: number | null;
  /** Your best lap in these races (seconds) and its car. */
  bestLap: number | null;
  bestLapCar: string | null;
  /** Your best lap ever (Torn's racing records), and its car. */
  recordLap: number | null;
  recordCar: string | null;
  skill: number;
  points: number;
  last: number | null;
}

/**
 * One row per track in Torn's order (every track, raced or not), then any track Torn's list doesn't name, then races
 * whose track isn't known (log lines that don't say).
 */
export function trackRows(races: readonly MyRace[], r: DateRange, tracks: readonly RaceTrack[] = RACE_TRACKS, records: readonly LapRecord[] = []): TrackRow[] {
  const shown = races.filter((x) => inRange(x.at, r));
  const rows = new Map<string, TrackRow>();
  const blank = (trackId: number | null, name: string): TrackRow => ({ trackId, name, races: 0, wins: 0, podiums: 0, crashes: 0, avgPosition: null, bestPosition: null, bestLap: null, bestLapCar: null, recordLap: null, recordCar: null, skill: 0, points: 0, last: null });
  for (const t of tracks) rows.set(`#${t.id}`, blank(t.id, t.name));
  const sums = new Map<string, { n: number; sum: number }>();
  for (const x of shown) {
    const key = x.trackId === null ? '?' : `#${x.trackId}`;
    let row = rows.get(key);
    if (!row) rows.set(key, (row = blank(x.trackId, x.trackId === null ? 'Track not said' : (trackOf(x.trackId, tracks)?.name ?? `Track ${x.trackId}`))));
    row.races++;
    if (won(x)) row.wins++;
    if (podium(x)) row.podiums++;
    if (x.crashed) row.crashes++;
    if (x.position !== null) {
      const s = sums.get(key) ?? { n: 0, sum: 0 };
      s.n++;
      s.sum += x.position;
      sums.set(key, s);
      row.bestPosition = row.bestPosition === null ? x.position : Math.min(row.bestPosition, x.position);
    }
    if (x.bestLap !== null && (row.bestLap === null || x.bestLap < row.bestLap)) {
      row.bestLap = x.bestLap;
      row.bestLapCar = x.car;
    }
    row.skill = round(row.skill + (x.skill ?? 0));
    row.points += x.points ?? 0;
    row.last = Math.max(row.last ?? 0, x.at);
  }
  for (const [key, s] of sums) rows.get(key)!.avgPosition = s.sum / s.n;
  for (const rec of records) {
    const row = rows.get(`#${rec.trackId}`) ?? (rows.set(`#${rec.trackId}`, blank(rec.trackId, trackOf(rec.trackId, tracks)?.name ?? rec.track)), rows.get(`#${rec.trackId}`)!);
    if (row.recordLap === null || rec.lap < row.recordLap) {
      row.recordLap = rec.lap;
      row.recordCar = rec.car;
    }
  }
  return [...rows.values()];
}

export interface CarRow {
  car: string;
  races: number;
  wins: number;
  podiums: number;
  crashes: number;
  tracks: number;
  bestPosition: number | null;
  /** Torn's own counters for that car (enlisted cars), all time. */
  entered: number | null;
  won: number | null;
  carClass: string | null;
  last: number | null;
}

/** The cars you raced in the range (most raced first), with Torn's all-time counters for your enlisted cars beside them. */
export function carRows(races: readonly MyRace[], r: DateRange, enlisted: readonly EnlistedCar[] = []): CarRow[] {
  const rows = new Map<string, CarRow & { trackSet: Set<string> }>();
  for (const x of races.filter((y) => inRange(y.at, r))) {
    const name = x.car ?? 'Car not said';
    let row = rows.get(name);
    if (!row) rows.set(name, (row = { car: name, races: 0, wins: 0, podiums: 0, crashes: 0, tracks: 0, bestPosition: null, entered: null, won: null, carClass: x.carClass, last: null, trackSet: new Set() }));
    row.races++;
    if (won(x)) row.wins++;
    if (podium(x)) row.podiums++;
    if (x.crashed) row.crashes++;
    if (x.position !== null) row.bestPosition = row.bestPosition === null ? x.position : Math.min(row.bestPosition, x.position);
    row.trackSet.add(String(x.trackId));
    row.carClass ??= x.carClass;
    row.last = Math.max(row.last ?? 0, x.at);
  }
  // Torn's counters: by the car's model name (several enlisted cars of one model add up).
  for (const c of enlisted) {
    const row = rows.get(c.model) ?? rows.get(c.name);
    if (!row) continue;
    row.entered = (row.entered ?? 0) + c.entered;
    row.won = (row.won ?? 0) + c.won;
    row.carClass ??= c.carClass;
  }
  return [...rows.values()]
    .map(({ trackSet, ...row }) => ({ ...row, tracks: trackSet.size }))
    .sort((a, b) => b.races - a.races || a.car.localeCompare(b.car));
}

export type RaceMeasure = 'races' | 'wins' | 'skill' | 'points';

/** Per Torn day: races, wins, skill or points. */
export function racesPerDay(races: readonly MyRace[], loose: readonly RaceLogLine[], measure: RaceMeasure, r: DateRange, now: number): Array<{ at: number; v: number }> {
  if (measure === 'races') return sumPerDay(races, () => 1, r, now);
  if (measure === 'wins') return sumPerDay(races, (x) => (won(x) ? 1 : 0), r, now);
  const items: Array<{ at: number; v: number }> = [...races.map((x) => ({ at: x.at, v: (measure === 'skill' ? x.skill : x.points) ?? 0 })), ...loose.map((x) => ({ at: x.at, v: (measure === 'skill' ? x.skill : x.points) ?? 0 }))];
  return sumPerDay(items, (x) => x.v, r, now).map((p) => ({ at: p.at, v: round(p.v) }));
}

/** The day a race happened (Torn day start), for grouping. */
export const raceDay = (r: MyRace): number => dayStart(r.at);

export function racesCsvRows(races: readonly MyRace[], trackName: (id: number | null) => string): { header: string[]; rows: Array<Array<string | number | null>> } {
  return {
    header: ['time_utc', 'race_id', 'title', 'track', 'laps', 'official', 'position', 'racers', 'crashed', 'car', 'car_class', 'race_time_s', 'best_lap_s', 'skill_gained', 'points', 'source'],
    rows: races.map((r) => [new Date(r.at).toISOString(), r.id, r.title, trackName(r.trackId), r.laps, r.official === null ? '' : r.official ? 'yes' : 'no', r.position, r.racers, r.crashed ? 'yes' : 'no', r.car, r.carClass, r.time, r.bestLap, r.skill, r.points, r.from]),
  };
}

/** "1:42.35" from seconds. */
export function lapText(sec: number | null): string {
  if (sec === null || !Number.isFinite(sec)) return '—';
  const m = Math.floor(sec / 60);
  const s = sec - m * 60;
  return `${m}:${s.toFixed(2).padStart(5, '0')}`;
}
